<template>
	<div class="tw">
		<div
			class="shared-textfield relative grid items-center"
			:class="{ 'is-invalid': isValidationVisible, 'is-readonly': readonly, 'is-disabled': disabled }"
		>
			<label class="shared-textfield-label ocm-body1 relative z-10 mx-3 -mb-3 w-fit bg-white px-1" :for="name">
				{{ label }}<span v-if="required && requiredFlag" aria-hidden="true">*</span>
			</label>

			<input
				:id="name"
				ref="inputRef"
				:aria-describedby="ariaDescribedBy"
				:autocomplete="autocomplete"
				:autocapitalize="autocapitalize"
				:autocorrect="autocorrect"
				class="shared-textfield-input col-start-1 row-start-2 h-14 rounded text-base"
				:class="{ 'has-currency': currency }"
				:[dataTestName]="''"
				:disabled="disabled"
				:inputmode="inputmode"
				:max="max"
				:maxlength="maxlength"
				:min="min"
				:name="name"
				:pattern="localPattern"
				:placeholder="placeholder"
				:readonly="readonly"
				:required="required"
				:spellcheck="props.type === 'email' ? 'false' : props.spellcheck"
				title=""
				:type="localType"
				:value="inputValue"
				:data-currency="currency"
				@focus="onFocus"
				@input="onInput"
				@change="onChange"
				@blur="onBlur"
			/>

			<div v-if="currency" class="pointer-events-none col-start-1 row-start-2 justify-self-start pl-4">
				{{ currency }}
			</div>

			<SharedButtonOnlyIcon
				v-if="type === 'password'"
				class="col-start-1 row-start-2 mr-1.5 size-10 justify-self-end focus-visible:outline-current"
				:icon-name="isPasswordVisible ? 'visibility-off' : 'visibility'"
				icon-size="5"
				:disabled="disabled"
				@click="togglePassword"
			/>
		</div>

		<div v-if="isHintVisible" :id="hintId" class="mt-1 text-xs text-gray-80">
			<slot />
		</div>

		<div
			v-if="isValidationVisible"
			:id="validationId"
			class="on-show-slide-in mt-1 grid grid-cols-[min-content,1fr] gap-x-1 text-xs text-error"
			:[dataTestErrorName]="''"
			aria-live="polite"
		>
			<SvgIcon class="size-4" icon-name="error" title="Fehler:" />
			{{ errorMessage }}
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, toRef, computed, watch, onMounted, onUpdated, inject, useSlots } from "vue";
import SharedButtonOnlyIcon from "./SharedButtonOnlyIcon.vue";
import SvgIcon from "./SvgIcon/SvgIcon.vue";
import useInputValidation from "../composables/use-input-validation";
import type { HTMLInputElementEvent, InputEventDetails } from "../types/SharedInput.type";

const emit = defineEmits<{
	(e: "update:modelValue", value: string): void;
	(e: "focus", event: FocusEvent & { target: HTMLInputElement }, details: InputEventDetails): void;
	(e: "input", event: HTMLInputElementEvent, details: InputEventDetails): void;
	(e: "change", event: HTMLInputElementEvent, details: InputEventDetails): void;
	(e: "blur", event: HTMLInputElementEvent, details: InputEventDetails): void;
	(e: "form-submit", event: Event & { target: HTMLFormElement }, details: InputEventDetails): void;
}>();

const props = withDefaults(
	defineProps<{
		autocomplete?: string;
		autocapitalize?: "off" | "none" | "on" | "sentences" | "words" | "characters";
		autocorrect?: "on" | "off";
		currency?: string;
		dataTestName?: string;
		disabled?: boolean;
		displayName?: string;
		forcedErrorMessage?: string;
		hintPermanent?: boolean;
		inputmode?: "none" | "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url";
		label: string;
		max?: string | number;
		maxlength?: string | number;
		min?: string | number;
		modelValue?: string | number;
		name: string;
		pattern?: string;
		patternErrorMessage?: string;
		placeholder?: string;
		readonly?: boolean;
		required?: boolean;
		requiredErrorMessage?: string;
		requiredFlag?: boolean;
		spellcheck?: boolean;
		type?: "text" | "number" | "email" | "tel" | "date" | "password";
		validateOnChange?: boolean;
		validateOnBlur?: boolean;
		value?: string;
	}>(),
	{ dataTestName: "", requiredFlag: true, type: "text", value: "" }
);

const inputRef = ref<HTMLInputElement | null>(null);
defineExpose({ inputRef });

const useVModel = computed(() => typeof props.modelValue !== "undefined");
const inputValue = useVModel.value
	? computed({
			get() {
				return String(props.modelValue ?? "");
			},
			set(val: string) {
				emit("update:modelValue", val);
			},
	  })
	: ref(props.value);
watch(
	() => props.value,
	(val) => {
		inputValue.value = val;
	}
);

/** Validation, Hint, Accessibility */

const UNIQUE_ID = `input-${Math.random().toString(36).slice(2, 9)}`;
const validationId = `validation-${UNIQUE_ID}`;
const { isValidationVisible, errorMessage, handleInput, handleChange, handleBlur } = useInputValidation(
	inputRef,
	props.displayName || props.name || "Feld",
	{
		patternErrorMessage: props.patternErrorMessage,
	},
	toRef(props, "forcedErrorMessage"),
	props.requiredErrorMessage
);

const hintId = `hint-${UNIQUE_ID}`;
const isHintVisible = computed(() => !!useSlots().default && (props.hintPermanent || !isValidationVisible.value));

const ariaDescribedBy = computed(() => {
	if (isHintVisible.value || isValidationVisible.value) {
		return `${isHintVisible.value ? hintId : ""} ${isValidationVisible.value ? validationId : ""}`.trim();
	}
	return undefined;
});

const dataTestErrorName = computed(() => (props.dataTestName ? `${props.dataTestName}-error` : ""));

/** Type Password */

const localType = ref(props.type);
const isPasswordVisible = ref(false);
const togglePassword = () => {
	isPasswordVisible.value = !isPasswordVisible.value;
	localType.value = isPasswordVisible.value ? "text" : "password";
};

/** Email Pattern */

// Hybris-RegEx für E-Mail, s. DefaultConfigValues.java: ^[\\w.+\\-]+@(?!xn--)[\\w.\\-]+(\\.[A-Za-z]{2,})$
// TUB-18168: ergänzt durch "." nach dem "@"
const EMAIL_PATTERN = "^[\\w.+\\-]+@(?!xn--|\\.)[\\w.\\-]+(\\.[A-Za-z]{2,})$";
const localPattern = ref(props.pattern);
const _setLocalPattern = () => {
	localPattern.value = props.type === "email" && !props.pattern ? EMAIL_PATTERN : props.pattern;
};
onMounted(() => {
	_setLocalPattern();
});
onUpdated(() => {
	_setLocalPattern();
});

/** Events */

const onFocus = (e: FocusEvent) => {
	emit("focus", e as FocusEvent & { target: HTMLInputElement }, { errorMessage: errorMessage.value });
};

const onInput = (e: Event) => {
	const val = (e.target as HTMLInputElement).value;
	if (useVModel.value) {
		emit("update:modelValue", val);
	} else {
		inputValue.value = val;
	}

	handleInput(); // Validation
	emit("input", e as HTMLInputElementEvent, { errorMessage: errorMessage.value });
};

const onChange = (e: Event) => {
	if (!props.validateOnBlur) handleChange(props.validateOnChange); // Validation
	emit("change", e as HTMLInputElementEvent, { errorMessage: errorMessage.value });
};

const onBlur = (e: Event) => {
	if (props.validateOnBlur) handleBlur(props.validateOnBlur); // Validation
	emit("blur", e as HTMLInputElementEvent, { errorMessage: errorMessage.value });
};

/** Form Submit */

const formRef = inject("formRef", ref<HTMLFormElement | null>(null));
if (formRef.value) {
	formRef.value.addEventListener("submit", handleSubmit);
} else {
	watch(formRef, () => {
		formRef.value?.addEventListener("submit", handleSubmit);
	});
}

function handleSubmit(e: Event) {
	emit("form-submit", e as Event & { target: HTMLFormElement }, { errorMessage: errorMessage.value });
}
</script>

<style lang="postcss">
.shared-textfield {
	--placeholder-color: var(--color-gray-80);
	--state-color: var(--placeholder-color);
	color: var(--state-color);

	&:is(:hover, :focus-within) {
		--placeholder-color: var(--color-black-primary);
	}

	&.is-invalid {
		--state-color: var(--color-error);
	}

	&:is(.is-readonly, .is-disabled) {
		--placeholder-color: var(--color-gray-50);
	}

	.iti__tel-input,
	> .shared-textfield-input {
		appearance: none;
		width: 100%;
		border-width: 1px;
		border-color: var(--state-color);
		padding-inline: 1.0714285rem;
		background-color: var(--color-white);
		color: var(--color-black-primary);

		&::placeholder {
			opacity: 1;
			color: var(--placeholder-color);
		}

		&:not(:read-only):focus-visible {
			box-shadow: 0 0 0 2px var(--state-color) inset;
		}

		&:is(:read-only, :disabled) {
			color: var(--color-gray-50);
			cursor: not-allowed;
		}

		&::-webkit-date-and-time-value {
			text-align: left;
		}

		&[data-currency] {
			padding-left: 2.2142857rem;
		}

		&[data-currency="CHF"] {
			padding-left: 3.9285714rem;
		}
	}
}

.on-show-slide-in {
	opacity: unset;
	translate: unset;
	transition-property: opacity, translate;
	transition-duration: 120ms;
	transition-timing-function: ease-out;

	@starting-style {
		opacity: 0;
		translate: 0 -25%;
	}
}
</style>
